import { useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import Services from "../../../../../services/Services";
import { useLanguage } from "../../../../../LanguageProvider";
import { GeneralContext } from "../../../../../GeneralProvider";
import io from "socket.io-client";
import UniversalTable from "../../../../../components/UniversalTable/UniversalTable";
import StatsHeader from "../../../components/StatsHeader/StatsHeader";
import UserSearchInput from "../../../../ProjectsWindow/components/ProjectAuthorization/components/UserSearchInput";
import ExportToExcelButton from "../../../../../components/ExportToExcelButton/ExportToExcelButton";
import Dropdown from "../../../../../components/Dropdown/Dropdown";
import DatePicker from "../../../../../components/DatePicker/DatePicker";
import AlertBox from "../../../../../components/AlertBox/AlertBox";
import "./SHEventSoldTickets.css";

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL;
const CANCELLED_FILTER = "__CANCELLED__";
const ITEMS_PER_PAGE = 25;

export default function SHEventSoldTickets({ event }) {
    const token = localStorage.getItem("token");
    const { setCreateProjectLoading } = useContext(GeneralContext);
    const { getTranslation, language } = useLanguage();

    const [tickets, setTickets] = useState([]);
    const [typeFilter, setTypeFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [dateRangeFilter, setDateRangeFilter] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [query, setQuery] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [isCheckInAlertOpen, setIsCheckInAlertOpen] = useState(false);
    const [ticketToCheckIn, setTicketToCheckIn] = useState(null);

    const socketRef = useRef(null);

    useEffect(() => {
        socketRef.current = io(SOCKET_URL, {
            reconnection: true,
            reconnectionAttempts: Infinity,
            transports: ["websocket"],
        });

        return () => {
            try {
                socketRef.current?.disconnect();
            } catch (_) { }
        };
    }, []);

    const getSoldTicketList = () => {
        setCreateProjectLoading(true);
        Services.SocialHiveGetEventSoldTickets(token, event?.eventID)
            .then((res) => {
                setCreateProjectLoading(false);
                const allTickets = res?.data?.result || [];
                const sortedTickets = allTickets.sort((a, b) => {
                    const usedA = a.isUsed === "1" ? 1 : 0;
                    const usedB = b.isUsed === "1" ? 1 : 0;
                    if (usedA !== usedB) return usedA - usedB;
                    const timeA = Number(a.paymentDateTime);
                    const timeB = Number(b.paymentDateTime);
                    return timeB - timeA;
                });
                setTickets(sortedTickets);
            })
            .catch((err) => {
                console.error(err);
                setCreateProjectLoading(false);
            });
    };

    const formatDate = useCallback((timestamp) => {
        if (!timestamp) return "-";
        const n = Number.parseInt(timestamp, 10);
        if (!Number.isFinite(n)) return "-";
        const date = new Date(n * 1000);
        return new Intl.DateTimeFormat(language, {
            timeZone: "Europe/Istanbul",
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        }).format(date);
    }, [language]);

    const getStatusInfo = useCallback((row) => {
        if (row.isRefund === "1") return { text: getTranslation("refunded"), type: "refunded" };
        if (row.status === "0") return { text: getTranslation("cancelled"), type: "failed" };
        if (row.isUsed === "1") return { text: getTranslation("used"), type: "completed" };
        return { text: getTranslation("notUsed"), type: "pending" };
    }, [getTranslation]);

    useEffect(() => {
        getSoldTicketList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setCurrentPage(1);
    }, [typeFilter, statusFilter, dateRangeFilter, query]);

    // Scroll to top when page changes
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [currentPage]);

    const handleUseTicket = async (ticket) => {
        try {
            if (ticket?.isUsed === "1") return;
            const res = await Services.SocialHiveUseTicket(token, ticket.ticketNo);
            if (res?.data?.status) {
                const serverUpdated = res?.data?.result;
                const usedTs = Math.floor(Date.now() / 1000);

                setTickets((prev) =>
                    prev.map((t) => {
                        if (t.ticketNo !== ticket.ticketNo) return t;
                        if (serverUpdated) {
                            return { ...t, ...serverUpdated, isUsed: "1" };
                        }
                        return { ...t, isUsed: "1", usedDateTime: String(usedTs) };
                    })
                );

                const roomName = `ticket-${ticket.userID}`;
                socketRef.current?.emit("joinRoom", roomName);
                const payload = {
                    module: "social-hive",
                    type: "use-ticket",
                    roomID: roomName,
                    content: {
                        message: "Ticket marked as USED and room joined.",
                        ticketNo: ticket.ticketNo,
                        userID: ticket.userID,
                        status: "used",
                        ticketID: ticket.ticketID,
                    },
                    createdDateTime: Math.floor(Date.now() / 1000),
                };
                socketRef.current?.emit("message", { room: roomName, message: payload });
            }
        } catch (err) {
            console.error("UseTicket error:", err);
        }
    };

    // Get unique ticket types for filter
    const ticketTypes = useMemo(() => {
        const types = new Set();
        tickets.forEach(t => {
            if (t.type || t.title) types.add(t.type || t.title);
        });
        return Array.from(types).map(type => ({ value: type, label: type }));
    }, [tickets]);

    // Create a map of ticketID to price for quick lookups
    const ticketPriceMap = useMemo(() => {
        const map = new Map();
        (event?.tickets || []).forEach(ticket => {
            if (ticket.ticketID) {
                map.set(String(ticket.ticketID), Number(ticket.price) || 0);
            }
        });
        return map;
    }, [event?.tickets]);

    const statusOptions = [
        { value: "", label: getTranslation("all") || "All" },
        { value: "used", label: getTranslation("used") || "Used" },
        { value: "notUsed", label: getTranslation("notUsed") || "Not Used" },
        { value: "cancelled", label: getTranslation("cancelled") || "Cancelled" },
        { value: "refunded", label: getTranslation("refunded") || "Refunded" },
    ];

    const dateRangeOptions = [
        { value: "", label: getTranslation("all") || "All" },
        { value: "today", label: getTranslation("today") || "Today" },
        { value: "last7Days", label: getTranslation("last7Days") || "Last 7 Days" },
        { value: "last30Days", label: getTranslation("last30Days") || "Last 30 Days" },
        { value: "custom", label: getTranslation("customRange") || "Custom Range" },
    ];

    const formatDateForFilter = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    };

    const handleDateRangeChange = (e) => {
        const value = e.target.value;
        setDateRangeFilter(value);
        const today = new Date();
        
        if (value === "today") {
            const todayStr = formatDateForFilter(today);
            setStartDate(todayStr);
            setEndDate(todayStr);
        } else if (value === "last7Days") {
            const sevenDaysAgo = new Date(today);
            sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
            setStartDate(formatDateForFilter(sevenDaysAgo));
            setEndDate(formatDateForFilter(today));
        } else if (value === "last30Days") {
            const thirtyDaysAgo = new Date(today);
            thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
            setStartDate(formatDateForFilter(thirtyDaysAgo));
            setEndDate(formatDateForFilter(today));
        } else if (value !== "custom") {
            setStartDate("");
            setEndDate("");
        }
    };

    const columns = [
        {
            key: "user",
            header: getTranslation("user") || "User",
            render: (row) => `${row.name || ""} ${row.lastname || ""}`.trim() || "-",
        },
        {
            key: "email",
            header: getTranslation("email") || "Email",
            render: (row) => row.email || "-",
        },
        {
            key: "phone",
            header: getTranslation("phone") || "Phone",
            render: (row) => row.phone || "-",
        },
        {
            key: "type",
            header: getTranslation("type") || "Type",
            render: (row) => {
                const typeName = row.title || row.type || getTranslation("other") || "Other";
                const typeKey = (row.type || row.title || "other").toLowerCase().replace(/\s+/g, "-");
                return (
                    <span className={`sh-ticket-type-link sh-ticket-type--${typeKey}`}>
                        {typeName}
                    </span>
                );
            },
        },
        {
            key: "amount",
            header: getTranslation("amount") || "Amount",
            render: (row) => {
                const ticketID = String(row.ticketID || "");
                const individualPrice = ticketPriceMap.get(ticketID);
                
                // If we found the ticket price, use it; otherwise fall back to paid/totalPaid
                const amount = individualPrice !== undefined 
                    ? individualPrice 
                    : (row.paid || row.totalPaid || "0");
                
                return Number(amount).toLocaleString("en-US");
            },
        },
        {
            key: "dateTime",
            header: getTranslation("dateTime") || "Date & Time",
            render: (row) => formatDate(row.paymentDateTime),
        },
        {
            key: "actions",
            header: getTranslation("checkIn") || "Check In",
            width: 100,
            render: (row) => {
                const isUsed = row.isUsed === "1";
                const canToggle = row.status !== "0" && row.isRefund !== "1";

                return (
                    <div className="sh-ticket-toggle-cell">
                        <button
                            className={`sh-ticket-toggle-button ${isUsed ? "sh-ticket-toggle-button--checked" : ""}`}
                            onClick={() => {
                                if (!isUsed && canToggle) {
                                    setTicketToCheckIn(row);
                                    setIsCheckInAlertOpen(true);
                                }
                            }}
                            disabled={!canToggle || isUsed}
                        />
                    </div>
                );
            },
        },
    ];

    const getRowKey = (row, idx) => String(row.id ?? row.ticketNo ?? idx);

    const norm = (val) =>
        (val ?? "")
            .toString()
            .toLocaleLowerCase("tr-TR")
            .trim();

    const indexedTickets = useMemo(() => {
        return tickets.map((t) => {
            const fields = [
                t.name,
                t.lastname,
                t.email,
                t.phone,
                t.paid,
                t.ticketNo,
                formatDate(t.paymentDateTime),
                t.title,
                t.type,
                getStatusInfo(t).text,
            ];
            return { ...t, _searchStr: norm(fields.join(" ")) };
        });
    }, [tickets, getStatusInfo, formatDate]);

    const displayedTickets = useMemo(() => {
        let list = indexedTickets;

        // Type filter
        if (typeFilter) {
            list = list.filter((t) => (t.type || t.title) === typeFilter);
        }

        // Status filter
        if (statusFilter) {
            if (statusFilter === "used") {
                list = list.filter((t) => t.isUsed === "1");
            } else if (statusFilter === "notUsed") {
                list = list.filter((t) => t.isUsed !== "1" && t.status !== "0" && t.isRefund !== "1");
            } else if (statusFilter === "cancelled") {
                list = list.filter((t) => t.status === "0");
            } else if (statusFilter === "refunded") {
                list = list.filter((t) => t.isRefund === "1");
            }
        }

        // Date range filter
        if (dateRangeFilter && startDate && endDate) {
            list = list.filter((t) => {
                if (!t.paymentDateTime) return false;
                const ticketDate = formatDateForFilter(new Date(parseInt(t.paymentDateTime, 10) * 1000));
                return ticketDate >= startDate && ticketDate <= endDate;
            });
        }

        // Search query
        const q = norm(query);
        if (q) {
            list = list.filter((t) => t._searchStr.includes(q));
        }

        return list;
    }, [indexedTickets, typeFilter, statusFilter, dateRangeFilter, startDate, endDate, query]);

    // Pagination
    const totalPages = Math.ceil(displayedTickets.length / ITEMS_PER_PAGE);
    const paginatedTickets = useMemo(() => {
        const start = (currentPage - 1) * ITEMS_PER_PAGE;
        const end = start + ITEMS_PER_PAGE;
        return displayedTickets.slice(start, end);
    }, [displayedTickets, currentPage]);

    const exportToExcel = async () => {
        try {
            const content = displayedTickets?.map((t, index) => {
                const statusInfo = getStatusInfo(t);
                const ticketID = String(t?.ticketID || "");
                const individualPrice = ticketPriceMap.get(ticketID);
                const amount = individualPrice !== undefined 
                    ? individualPrice 
                    : (t?.paid ?? "-");
                return {
                    id: index + 1,
                    ticketNo: t?.ticketNo ?? "",
                    name: `${t?.name ?? ""} ${t?.lastname ?? ""}`.trim(),
                    email: t?.email ?? "",
                    phone: t?.phone ?? "",
                    type: t?.title ?? t?.type ?? "",
                    amount: amount,
                    status: statusInfo.text,
                    date: formatDate(t?.paymentDateTime),
                };
            });

            const header = {
                ID: "number",
                [getTranslation("ticketNo")]: "string",
                [getTranslation("user")]: "string",
                [getTranslation("email")]: "string",
                [getTranslation("phone")]: "string",
                [getTranslation("type")]: "string",
                [getTranslation("amount")]: "string",
                [getTranslation("status")]: "string",
                [getTranslation("dateTime")]: "string",
            };

            const sheetTitle = getTranslation("soldTickets");

            const res = await Services.ExportToExcel(token, content, header, sheetTitle);

            if (res?.data?.status) {
                const fileUrl = res?.data?.result;
                if (fileUrl) {
                    window.open(fileUrl.startsWith("http") ? fileUrl : `http://${fileUrl}`, "_blank");
                }
            } else {
                console.log(res?.data?.message || "Export failed");
            }
        } catch (err) {
            console.log(err);
        }
    };

    return (
        <div className="sh-settings-main">
            <div className="sh-settings-content">
                <span className="sh-settings-title">{getTranslation("soldTickets")}</span>

                <StatsHeader
                    tickets={tickets}
                    event={event}
                    currency="TRY"
                    onTypeFilterChange={setTypeFilter}
                    activeType={typeFilter}
                    __cancelledKey={CANCELLED_FILTER}
                />

                <div className="sh-sold-tickets-filters">
                    <UserSearchInput
                        placeholder={getTranslation("searchByUserEmailTicketId")}
                        value={query}
                        onSearch={setQuery}
                        style={{ flex: 1, maxWidth: "none" }}
                    />

                    <Dropdown
                        placeholder={getTranslation("ticketType")}
                        options={[
                            { value: "", label: getTranslation("all") },
                            ...ticketTypes
                        ]}
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        style={{ width: "180px" }}
                    />

                    <Dropdown
                        placeholder={getTranslation("status")}
                        options={statusOptions}
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        style={{ width: "150px" }}
                    />

                    <div className="sh-date-range-filter">
                        <Dropdown
                            placeholder={getTranslation("dateRange")}
                            options={dateRangeOptions}
                            value={dateRangeFilter}
                            onChange={handleDateRangeChange}
                            style={{ width: "160px" }}
                        />
                        {dateRangeFilter === "custom" && (
                            <div className="sh-custom-date-pickers">
                                <DatePicker
                                    onDateSelect={(date) => setStartDate(formatDateForFilter(date))}
                                    toggleLabel={getTranslation("startDate")}
                                    buttonStyle={{ width: "150px" }}
                                />
                                <DatePicker
                                    onDateSelect={(date) => setEndDate(formatDateForFilter(date))}
                                    toggleLabel={getTranslation("endDate")}
                                    buttonStyle={{ width: "150px" }}
                                />
                            </div>
                        )}
                    </div>

                    <ExportToExcelButton onClick={exportToExcel} />
                </div>

                <UniversalTable
                    data={paginatedTickets}
                    columns={columns}
                    getRowKey={getRowKey}
                    tableClassName="sh-settings-table sh-transactions-table"
                    emptyText={getTranslation("noData")}
                    containerClassName="sh-sold-tickets-table-wrapper"
                />

                {displayedTickets.length > 0 && (
                    <div className="sh-transactions-pagination">
                        <button
                            className="sh-pagination-button sh-pagination-button--prev"
                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={currentPage === 1}
                        >
                            {getTranslation("previous") || "Previous"}
                        </button>
                        <button
                            className="sh-pagination-button sh-pagination-button--next"
                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                            disabled={currentPage === totalPages}
                        >
                            {getTranslation("next") || "Next"}
                        </button>
                        <div className="sh-pagination-info">
                            {getTranslation("showing") || "Showing"} {((currentPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, displayedTickets.length)} {getTranslation("of") || "of"} {displayedTickets.length} {getTranslation("transactions") || "transactions"}
                        </div>
                    </div>
                )}

                <AlertBox
                    isOpen={isCheckInAlertOpen}
                    title={getTranslation("areYouSure")}
                    message={getTranslation("markAsAttended")}
                    onConfirm={() => {
                        if (ticketToCheckIn) {
                            handleUseTicket(ticketToCheckIn);
                        }
                        setIsCheckInAlertOpen(false);
                        setTicketToCheckIn(null);
                    }}
                    onCancel={() => {
                        setIsCheckInAlertOpen(false);
                        setTicketToCheckIn(null);
                    }}
                    confirmText={getTranslation("yes")}
                    cancelText={getTranslation("no")}
                    type="warning"
                />
            </div>
        </div>
    );
}